/**
 * Music: the shared Spotify account, Sonos speakers, grouping and now playing.
 *
 * The server (server/src/modules/spotify.ts) talks to the Spotify Web API with
 * the house account's token and returns these compact shapes; the token never
 * reaches the browser. Pure helpers (device matching, now-playing selection,
 * proportional group volume) live here so server, web and tests share them.
 */
import { z } from 'zod';
import { EntityId } from './model.js';
export const PlayMethod = z.enum(['auto', 'connect', 'play_media']);
const SpotifyUri = z.string().regex(/^spotify:(track|album|playlist|artist):[A-Za-z0-9]{8,40}$/);
export const PlayInput = z.object({
    entityId: EntityId,
    uri: SpotifyUri,
    /** Start a context at this track. */
    offsetUri: SpotifyUri.optional(),
    method: PlayMethod.optional(),
});
export const QueueInput = z.object({ entityId: EntityId, uri: SpotifyUri });
/** Song radio on a speaker, seeded by `trackUri` or (default) the speaker's current Spotify track. */
export const RadioInput = z.object({
    entityId: EntityId,
    trackUri: z.string().regex(/^spotify:track:[A-Za-z0-9]{8,40}$/).optional(),
});
export const GroupVolumeInput = z.object({ entityId: EntityId, volume: z.number().min(0).max(1) });
export const SpeakerMappingInput = z.array(z.object({
    entityId: EntityId,
    deviceName: z.string().trim().max(120).nullable(),
    method: PlayMethod,
})).max(64);
// ---------------------------------------------------------------- pure helpers
export const spotifyUriKind = (uri) => {
    const m = /^spotify:(track|album|playlist|artist):/.exec(uri);
    return m ? m[1] : null;
};
const decodeLoose = (s) => {
    try {
        return decodeURIComponent(s);
    }
    catch {
        return s.replace(/%3a/gi, ':');
    }
};
/**
 * The Spotify track id in a media_player's `media_content_id`, or null. Sonos reports
 * `x-sonos-spotify:spotify%3atrack%3a<id>?sid=12&flags=8232&sn=3` for queue/radio items;
 * also accepted: `spotify:track:<id>`, `spotify://track:<id>`, open.spotify.com links.
 * A Spotify Connect session on Sonos (`x-sonos-vli:RINCON_…:2,spotify:<session>`) has no track id.
 */
export function spotifyTrackIdFrom(contentId) {
    if (typeof contentId !== 'string' || !contentId || contentId.length > 600)
        return null;
    if (/^x-sonos-vli:/i.test(contentId))
        return null;
    const s = decodeLoose(contentId.split('?')[0].replace(/^x-sonos-spotify:/i, ''));
    const m = /(?:^|[^a-z])spotify(?::|:\/\/)track:([A-Za-z0-9]{8,40})$/i.exec(s)
        ?? /^https?:\/\/open\.spotify\.com\/(?:intl-[a-z-]+\/)?track\/([A-Za-z0-9]{8,40})$/i.exec(s);
    return m ? m[1] : null;
}
/** Whatever is on the speaker comes from Spotify (a Sonos queue/radio item or a Connect session). */
export function isSpotifyContent(contentId, source) {
    if (spotifyTrackIdFrom(contentId) !== null)
        return true;
    if (typeof contentId === 'string' && /^x-sonos-vli:[^,]*,spotify:/i.test(contentId))
        return true;
    return typeof source === 'string' && source.toLowerCase() === 'spotify' && typeof contentId !== 'string';
}
/** The Sonos music-service account (`sid`, `sn`) of a Spotify item URI the speaker played, e.g. x-sonos-spotify:…?sid=12&…&sn=3. */
export function sonosSpotifyAccount(contentId) {
    if (typeof contentId !== 'string' || !/^(?:x-sonos-spotify|x-sonosapi-radio|x-rincon-cpcontainer):/i.test(contentId))
        return null;
    if (!/spotify/i.test(contentId.split('?')[0]))
        return null;
    const q = new URLSearchParams(contentId.split('?')[1] ?? '');
    const sid = Number(q.get('sid'));
    const sn = Number(q.get('sn'));
    return Number.isInteger(sid) && sid > 0 && Number.isInteger(sn) && sn >= 0 && q.has('sn') ? { sid, sn } : null;
}
/**
 * Sonos's native Spotify radio station URI (what the Sonos app plays for "Artist Radio" /
 * "Track Radio"): `x-sonosapi-radio:spotify%3aartistRadio%3a<id>?sid=12&flags=8300&sn=3`. The id
 * is percent-encoded on purpose: HA's Sonos play_media first tries SoCo's share-link parser,
 * which would match a literal "spotify:track:<id>" inside the URI and play the bare track instead.
 */
export function sonosSpotifyRadioUri(kind, id, account) {
    return `x-sonosapi-radio:spotify%3a${kind}Radio%3a${encodeURIComponent(id)}?sid=${account.sid}&flags=8300&sn=${account.sn}`;
}
export function sonosPlaybackKind(contentId, state) {
    if (state === 'off' || state === 'unavailable' || typeof contentId !== 'string' || !contentId)
        return 'none';
    if (/^x-sonos-vli:/i.test(contentId))
        return /^x-sonos-vli:[^,]*,spotify:/i.test(contentId) ? 'connect' : 'none';
    if (/^(?:x-sonosapi-radio|x-sonosapi-stream|x-sonosprog(?:-http)?|x-rincon-mp3radio|aac|hls-radio):/i.test(contentId))
        return 'radio';
    // hls-static is a queued track (e.g. Apple Music); plain x-sonosapi-hls is a live stream.
    if (/^x-sonosapi-hls:/i.test(contentId))
        return 'radio';
    if (/^(?:x-rincon-stream|x-sonos-htastream|x-rincon):/i.test(contentId))
        return 'none';
    return 'queue';
}
/**
 * HA's sonos.get_queue response ({ "<entity_id>": [{ media_title, media_artist, media_album_name, media_content_id }] })
 * as queue items, split at the speaker's 1-based `queue_position` attribute (absent → the first item is current).
 */
export function parseSonosQueue(response, entityId, queuePosition, max = 30) {
    const byEntity = response && typeof response === 'object' ? response[entityId] : null;
    const rows = Array.isArray(byEntity) ? byEntity : [];
    const all = rows.map((r) => {
        if (!r || typeof r !== 'object')
            return null;
        const o = r;
        const title = typeof o['media_title'] === 'string' ? o['media_title'].trim() : '';
        if (!title)
            return null;
        const id = spotifyTrackIdFrom(o['media_content_id']);
        return {
            title,
            artist: typeof o['media_artist'] === 'string' && o['media_artist'] ? o['media_artist'] : null,
            album: typeof o['media_album_name'] === 'string' && o['media_album_name'] ? o['media_album_name'] : null,
            ...(id ? { uri: `spotify:track:${id}` } : {}),
        };
    });
    const pos = typeof queuePosition === 'number' && Number.isInteger(queuePosition) && queuePosition >= 1 ? queuePosition : 1;
    const current = all[pos - 1] ?? null;
    return { current, items: compact(all.slice(pos)).slice(0, max) };
}
/** Lowercase, accents and punctuation stripped, "sonos" dropped: "Living Room (Sonos)" → "living room". */
export function normalizeName(s) {
    return s
        .normalize('NFKD')
        .replace(/[̀-ͯ]/g, '')
        .toLowerCase()
        .replace(/\bsonos\b/g, ' ')
        .replace(/[^a-z0-9]+/g, ' ')
        .trim();
}
/**
 * Finds the Spotify Connect device for a speaker. A manual name (admin mapping)
 * must match exactly (normalised); otherwise the HA friendly name is tried
 * exactly, then as a whole-word prefix of the device name ("Kitchen" → "Kitchen Sonos One").
 * Ambiguous prefix matches return null rather than guess.
 */
export function matchSpotifyDevice(speakerName, devices, manual) {
    if (manual) {
        const want = normalizeName(manual);
        const d = devices.find((x) => normalizeName(x.name) === want);
        return d ? { device: d, match: 'manual' } : null;
    }
    const want = normalizeName(speakerName);
    if (!want)
        return null;
    const exact = devices.filter((x) => normalizeName(x.name) === want);
    if (exact.length >= 1) {
        const speaker = exact.find((x) => (x.type ?? '').toLowerCase() === 'speaker');
        return { device: speaker ?? exact[0], match: 'name' };
    }
    // Only the device name may be longer ("Kitchen" ↔ "Kitchen Sonos One"); never the other way,
    // or "Guest 1 Ensuite" would grab the "Guest 1" room's device.
    const prefix = devices.filter((x) => normalizeName(x.name).startsWith(`${want} `));
    return prefix.length === 1 ? { device: prefix[0], match: 'name' } : null;
}
/** The coordinator of a player's group (HA lists it first), or the player itself. */
export const coordinatorOf = (p) => p.members[0] ?? p.id;
/**
 * Which player the now-playing bar shows. Candidates are playing coordinators
 * (group members collapse into their coordinator), plus `sticky` while it is
 * paused or buffering, so pausing from the bar doesn't make it vanish.
 * Most recently started first; `preferred` (the switcher choice) wins when present.
 */
export function pickNowPlaying(players, opts = {}) {
    const byId = new Map(players.map((p) => [p.id, p]));
    const live = (p) => p.state === 'playing' || ((p.state === 'paused' || p.state === 'buffering') && (p.id === opts.sticky || coordinatorOf(p) === opts.sticky));
    const seen = new Set();
    const out = [];
    for (const p of players) {
        if (!live(p))
            continue;
        const coord = byId.get(coordinatorOf(p)) ?? p;
        if (seen.has(coord.id))
            continue;
        seen.add(coord.id);
        // A member reports the group's state; the coordinator's entry carries it too.
        out.push(coord.state === p.state ? coord : { ...coord, state: p.state, lc: Math.max(coord.lc, p.lc) });
    }
    out.sort((a, b) => b.lc - a.lc || a.id.localeCompare(b.id));
    const all = out.map((p) => p.id);
    const current = opts.preferred && all.includes(opts.preferred) ? opts.preferred : (all[0] ?? null);
    return { current, all };
}
/** Group volume = the loudest member, so the master slider's 100 % is the loudest speaker at full. */
export function groupVolumeOf(volumes) {
    return volumes.length ? Math.max(...volumes) : 0;
}
/**
 * Proportional group volume: every member scales by target / current master,
 * keeping their balance. From silence (master 0) every member goes to target.
 * Results are clamped to 0..1 and rounded to 2 dp (Sonos has 100 steps).
 */
export function scaleGroupVolume(members, target) {
    const t = Math.min(1, Math.max(0, target));
    const master = groupVolumeOf(members.map((m) => m.volume));
    return members.map((m) => {
        const v = master <= 0 ? t : (m.volume / master) * t;
        return { id: m.id, volume: Math.round(Math.min(1, Math.max(0, v)) * 100) / 100 };
    });
}
const s = (v) => (typeof v === 'string' ? v : '');
const arr = (v) => (Array.isArray(v) ? v.filter((x) => x && typeof x === 'object') : []);
/** The smallest image at least `min` px wide (else the largest). */
export function pickImage(images, min = 300) {
    const list = arr(images);
    if (!list.length)
        return null;
    const sized = [...list].sort((a, b) => (a.width ?? 0) - (b.width ?? 0));
    const ok = sized.find((i) => (i.width ?? 0) >= min);
    return (ok ?? sized[sized.length - 1] ?? list[0])?.url ?? null;
}
const names = (v) => arr(v).map((a) => a.name ?? '').filter(Boolean).join(', ');
export function mapTrack(t, albumFallback) {
    if (!t || !s(t['id']) || !s(t['uri']))
        return null;
    const album = t['album'] ?? albumFallback;
    const artists = arr(t['artists']);
    return {
        kind: 'track',
        id: s(t['id']),
        uri: s(t['uri']),
        name: s(t['name']),
        subtitle: names(artists),
        image: album ? pickImage(album['images'], 64) : null,
        ...(typeof t['duration_ms'] === 'number' ? { durationMs: t['duration_ms'] } : {}),
        ...(t['explicit'] === true ? { explicit: true } : {}),
        ...(album && s(album['id']) ? { albumId: s(album['id']), albumName: s(album['name']) } : {}),
        ...(artists[0] && s(artists[0]['id']) ? { artistId: s(artists[0]['id']) } : {}),
    };
}
export function mapAlbum(a) {
    if (!a || !s(a['id']) || !s(a['uri']))
        return null;
    const artists = arr(a['artists']);
    const year = s(a['release_date']).slice(0, 4);
    return {
        kind: 'album',
        id: s(a['id']),
        uri: s(a['uri']),
        name: s(a['name']),
        subtitle: names(artists),
        image: pickImage(a['images']),
        ...(year ? { meta: year } : {}),
        ...(artists[0] && s(artists[0]['id']) ? { artistId: s(artists[0]['id']) } : {}),
    };
}
export function mapPlaylist(p) {
    if (!p || !s(p['id']) || !s(p['uri']))
        return null;
    const owner = p['owner'];
    // Spotify renamed playlist `tracks` to `items` (2026); accept both.
    const count = (p['items'] ?? p['tracks'])?.['total'];
    return {
        kind: 'playlist',
        id: s(p['id']),
        uri: s(p['uri']),
        name: s(p['name']),
        subtitle: owner ? s(owner['display_name']) || s(owner['id']) : '',
        image: pickImage(p['images']),
        ...(typeof count === 'number' ? { meta: `${count} songs` } : {}),
    };
}
export function mapArtist(a) {
    if (!a || !s(a['id']) || !s(a['uri']))
        return null;
    return { kind: 'artist', id: s(a['id']), uri: s(a['uri']), name: s(a['name']), subtitle: 'Artist', image: pickImage(a['images']) };
}
export function mapAny(x) {
    switch (s(x?.['type'])) {
        case 'track': return mapTrack(x);
        case 'album': return mapAlbum(x);
        case 'playlist': return mapPlaylist(x);
        case 'artist': return mapArtist(x);
        default: return null;
    }
}
export const compact = (xs) => xs.filter((x) => x != null);
/** Distinct by URI, keeping the first. */
export function uniqueItems(items) {
    const seen = new Set();
    return items.filter((i) => (seen.has(i.uri) ? false : (seen.add(i.uri), true)));
}
//# sourceMappingURL=music.js.map